<template>
  <div class="monitor">
    <h1>🛰️ 同行监控截流</h1>

    <!-- 监控状态横幅 -->
    <el-alert v-if="monitorEnabled" type="success" :closable="false" show-icon style="margin-bottom: 15px">
      <template #title>🟢 同行监控运行中 — 每 {{ intervalMinutes }} 分钟自动监控 {{ targets.length }} 个目标</template>
    </el-alert>
    <el-alert v-else type="info" :closable="false" show-icon style="margin-bottom: 15px">
      <template #title>⚪ 同行监控已停止 — 在下方「调度设置」打开开关即可开启</template>
    </el-alert>

    <el-card>
      <template #header>
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center">
          <span>📋 监控目标（同行主页 / 作品链接）</span>
          <div>
            <el-button v-if="selectedTargets.length" type="danger" size="small" style="margin-right: 8px" @click="batchRemove">
              批量删除（{{ selectedTargets.length }}）
            </el-button>
            <el-button type="primary" size="small" @click="addTarget">＋ 添加目标</el-button>
          </div>
        </div>
      </template>

      <el-table :data="targets" stripe size="small" style="width: 100%" @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="目标名称" min-width="160" show-overflow-tooltip>
          <template #default="scope">
            <a v-if="scope.row && scope.row.url" :href="scope.row.url" target="_blank" rel="noopener" style="color:#409EFF; text-decoration:none">
              {{ typeof scope.row === 'string' ? scope.row : (scope.row.name || scope.row.url) }}
            </a>
            <span v-else>{{ typeof scope.row === 'string' ? scope.row : (scope.row.name || scope.row.url) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="同行链接（主页或作品）" min-width="300" show-overflow-tooltip>
          <template #default="scope">
            <a v-if="scope.row && scope.row.url" :href="scope.row.url" target="_blank" rel="noopener" style="color:#409EFF; text-decoration:none">{{ scope.row.url }}</a>
            <span v-else>{{ scope.row && scope.row.url }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="max_comments" label="采集评论数" width="110" align="center" />
        <el-table-column label="操作" width="120" align="center">
          <template #default="scope">
            <el-button type="danger" size="mini" @click="removeTarget(scope.$index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!targets.length" style="text-align:center; color:#909399; padding:20px">
        暂无监控目标 —— 录入同行主页或作品链接后，系统将按设定间隔自动监控新作品并截流意向客户
      </div>

      <!-- 添加目标表单 -->
      <el-form v-if="showForm" inline style="margin-top: 15px" @submit.prevent>
        <el-form-item label="链接">
          <el-input v-model="newUrl" placeholder="https://www.douyin.com/user/xxx 或 /video/xxx" style="width: 420px" clearable />
        </el-form-item>
        <el-form-item label="采集评论数">
          <el-input-number v-model="newMaxComments" :min="20" :max="500" :step="10" />
        </el-form-item>
        <el-form-item>
          <el-button type="success" @click="saveTarget">保存</el-button>
          <el-button @click="showForm = false">取消</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card style="margin-top: 20px">
      <template #header>
        <span>⚙️ 调度设置</span>
      </template>
      <el-form label-width="160px" style="max-width: 520px">
        <el-form-item label="监控总开关">
          <el-switch v-model="monitorEnabled" @change="saveSettings" />
          <span style="margin-left: 10px; color: #909399">开启后系统按间隔自动监控所有目标</span>
        </el-form-item>
        <el-form-item label="监控间隔（分钟）">
          <el-input-number v-model="intervalMinutes" :min="10" :max="1440" :step="10" @change="saveSettings" />
          <span style="margin-left: 10px; color: #909399">最小10分钟，建议30-60分钟</span>
        </el-form-item>
        <el-form-item label="直播截流门槛（在线人数）">
          <el-input-number v-model="liveMinViewers" :min="1" :max="500" @change="saveSettings" />
          <span style="margin-left: 10px; color: #909399">在线≥该人数的直播间才截流（默认15）</span>
        </el-form-item>
        <el-form-item>
          <el-button type="warning" @click="triggerRun">▶ 立即执行一轮监控</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card style="margin-top: 20px">
      <template #header>
        <span>🕐 最近监控任务</span>
      </template>
      <el-table :data="recentTasks" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="account_id" label="账号" width="80" align="center" />
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 'completed' ? 'success' : (row.status === 'running' ? 'warning' : 'info')" size="small">
              {{ row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果" min-width="320" show-overflow-tooltip>
          <template #default="{ row }">
            <span style="font-size: 12px">{{ parseResult(row.result) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="提交时间" width="170" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'Monitor',
  data() {
    return {
      targets: [],
      monitorEnabled: false,
      intervalMinutes: 30,
      liveMinViewers: 15,
      showForm: false,
      newUrl: '',
      newMaxComments: 60,
      recentTasks: [],
      selectedTargets: []
    }
  },
  created() {
    this.loadConfig()
    this.loadTasks()
  },
  methods: {
    async loadConfig() {
      try {
        const r = await api.get('/leads/monitor-config')
        this.targets = r.monitor_targets || []
        this.monitorEnabled = !!r.monitor_enabled
        this.intervalMinutes = r.monitor_interval_minutes || 30
        const s = await api.get('/leads/settings')
        this.liveMinViewers = s.live_min_viewers || 15
      } catch (e) {
        this.$message.error('加载配置失败: ' + e.message)
      }
    },
    async loadTasks() {
      try {
        const r = await api.get('/tasks?limit=8')
        const list = Array.isArray(r) ? r : (r.items || r.tasks || [])
        this.recentTasks = list.filter(t => t.task_type === 'monitor').slice(0, 8)
      } catch (e) {
        // 忽略
      }
    },
    addTarget() {
      this.showForm = true
    },
    async saveTarget() {
      if (!this.newUrl.trim()) {
        this.$message.warning('请输入同行主页或作品链接')
        return
      }
      const targets = [...this.targets, { url: this.newUrl.trim(), max_comments: this.newMaxComments }]
      const r = await api.post('/leads/monitor-config', { targets })
      this.targets = r.config.monitor_targets || []
      this.showForm = false
      this.newUrl = ''
      this.$message.success('监控目标已添加')
    },
    async removeTarget(idx) {
      const targets = this.targets.filter((_, i) => i !== idx)
      const r = await api.post('/leads/monitor-config', { targets })
      this.targets = r.config.monitor_targets || []
      this.$message.success('已删除')
    },
    onSelectionChange(rows) {
      this.selectedTargets = rows
    },
    async batchRemove() {
      if (!this.selectedTargets.length) return
      const before = this.targets.length
      const keep = this.targets.filter(t => !this.selectedTargets.includes(t))
      const r = await api.post('/leads/monitor-config', { targets: keep })
      this.targets = r.config.monitor_targets || []
      const removed = before - this.targets.length
      this.selectedTargets = []
      this.$message.success(`已批量删除 ${removed} 个目标`)
    },
    async saveSettings() {
      try {
        await api.post('/leads/settings', {
          live_min_viewers: this.liveMinViewers,
          monitor_interval_minutes: this.intervalMinutes,
          monitor_enabled: this.monitorEnabled
        })
        await api.post('/leads/monitor-config', {
          interval_minutes: this.intervalMinutes,
          enabled: this.monitorEnabled
        })
        this.$message.success('设置已保存')
      } catch (e) {
        this.$message.error('保存失败: ' + e.message)
      }
    },
    async triggerRun() {
      try {
        const r = await api.post('/leads/monitor-run?account_id=3')
        this.$message.success('监控任务已提交 (#' + r.task_id + ')')
        setTimeout(() => this.loadTasks(), 3000)
      } catch (e) {
        this.$message.error('触发失败: ' + e.message)
      }
    },
    parseResult(result) {
      if (!result) return ''
      try {
        const obj = JSON.parse(result)
        return obj.detail || JSON.stringify(obj).slice(0, 200)
      } catch (e) {
        return String(result).slice(0, 200)
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
